/**
 * Plain-text helpers for story bodies, shared by the server (word counts,
 * reading time, stripping editor embeds from member text, excerpts, slugs) and
 * the client (the write form's live count and the preview), so both sides
 * count the same way.
 *
 * The body format is deliberately tiny: a blank line starts a paragraph,
 * **bold**, "- " a bullet, "## " a heading, and — in team guides only — a
 * `{{name}}` line places an embed block.
 */

/** A `{{name}}` line (an editor-placed embed). */
export const EMBED_LINE = /^\{\{\s*([a-z0-9-]+)\s*\}\}$/;

/** Removes editor-only embed lines from member-written text. */
export function stripEmbeds(body: string): string {
  return body
    .split("\n")
    .filter((line) => !EMBED_LINE.test(line.trim()))
    .join("\n");
}

/** Words a reader will actually read: no markers, no embed lines. */
export function countWords(body: string): number {
  return stripEmbeds(body)
    .replace(/\*\*/g, "")
    .replace(/^\s*(?:[-*•]|#{2,3})\s+/gm, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

/** About 230 words a minute (an average adult reading silently), rounded up, at least one. */
export function readingMinutes(body: string): number {
  return Math.max(1, Math.ceil(countWords(body) / 230));
}

/**
 * The first paragraph of plain prose, without markers, cut at a word boundary
 * — the card summary for a member's story (members don't write a separate dek).
 */
export function excerpt(body: string, max = 180): string {
  const paragraph =
    stripEmbeds(body)
      .replace(/\r\n?/g, "\n")
      .split(/\n\s*\n/)
      .map((chunk) =>
        chunk
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line && !/^#{2,3}\s+/.test(line))
          .map((line) => line.replace(/^[-*•]\s+/, ""))
          .join(" "),
      )
      .find((chunk) => chunk.length > 0) ?? "";
  const clean = paragraph.replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max + 1);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut.slice(0, max)).replace(/[\s,;:.–—-]+$/, "")}…`;
}

/** "Ali came three weeks early!" → "ali-came-three-weeks-early" (ASCII only, ≤ 60 chars). */
export function slugifyTitle(title: string): string {
  const slug = title
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
  return slug || "story";
}
